import React, { useMemo, useState } from "react";
import {
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import KpiCard from "../commercial-dashboard/KpiCard";
import { KpiGrid, Section } from "../commercial-dashboard/DashboardBits";
import {
  MONTHS,
  Totals,
  aggregate,
  eur,
  groupBy,
  inPeriod,
  keur,
  pct,
  pctSigned,
  pp,
  sum,
  toneOfCompliance,
  toneOfMarginDiff,
} from "./controlBoardModel";
import { Num, Signal } from "./ControlBoardBits";
import { TabProps } from "./boardData";
import { ControlBoardRow } from "../../types/controlBoard";

type Measure = "sale" | "profit" | "margin";

/** Tablero: KPIs del año y matrices cliente × mes de facturación y beneficio. */
const BoardTab: React.FC<TabProps> = ({ data, onOpenClient }) => {
  const [profitUnit, setProfitUnit] = useState<"eur" | "pct">("eur");
  const rows = useMemo(() => inPeriod(data.rows, 0, data.lastMonth), [data]);
  const plan = useMemo(() => inPeriod(data.plan, 0, data.lastMonth), [data]);
  const yearPlan = sum(data.plan, (l) => l.managedInvestment);
  const yearPlanProfit = sum(data.plan, (l) => l.campaignProfit);
  const totals = aggregate(rows);
  const planToDate = sum(plan, (l) => l.managedInvestment);
  const planProfitToDate = sum(plan, (l) => l.campaignProfit);

  return (
    <Stack gap={2}>
      <KpiGrid columns={4}>
        <KpiCard
          label="Facturación real"
          value={eur(totals.sale)}
          caption={`${pct(totals.compliance, 0)} de lo previsto en briefings (${keur(totals.plannedSale)})${
            data.hasPlan
              ? ` · ${pct(planToDate ? totals.sale / planToDate : NaN, 0)} del BP a ${MONTHS[data.lastMonth].toLowerCase()} · ${pct(yearPlan ? totals.sale / yearPlan : NaN, 0)} del BP anual (${keur(yearPlan)})`
              : ""
          }`}
          tone={toneToCard(toneOfCompliance(totals.compliance))}
        />
        <KpiCard
          label="Beneficio bruto real"
          value={eur(totals.profit)}
          caption={`${pct(totals.profitCompliance, 0)} de lo previsto (${keur(totals.plannedProfit)})${
            data.hasPlan
              ? ` · ${pct(planProfitToDate ? totals.profit / planProfitToDate : NaN, 0)} del BP a la fecha · BP anual ${keur(yearPlanProfit)}`
              : ""
          }`}
          tone={toneToCard(toneOfCompliance(totals.profitCompliance))}
        />
        <KpiCard
          label="Margen bruto real"
          value={pct(totals.margin)}
          caption={`${pp(totals.margin - totals.plannedMargin)} frente al ${pct(totals.plannedMargin)} previsto`}
          tone={toneToCard(
            toneOfMarginDiff(totals.margin - totals.plannedMargin)
          )}
        />
        <KpiCard
          label="Clientes activos"
          value={String(new Set(rows.map((r) => r.clientId)).size)}
          caption={`${rows.length.toLocaleString("es-ES")} líneas × mes · enero a ${MONTHS[data.lastMonth].toLowerCase()}`}
        />
      </KpiGrid>

      <Section
        title="Facturación por cliente y mes"
        subtitle="Previsto en briefings, real y desviación de cada mes. Pulsa un cliente para ver su detalle."
      >
        <Matrix
          data={data}
          rows={rows}
          measure="sale"
          onOpenClient={onOpenClient}
        />
      </Section>

      <Section
        title="Beneficio bruto por cliente y mes"
        subtitle={
          profitUnit === "pct"
            ? "Margen previsto y real de cada mes. El color compara el real con el previsto."
            : "Previsto, real y desviación de cada mes."
        }
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={profitUnit}
            onChange={(_, v) => v && setProfitUnit(v)}
          >
            <ToggleButton value="eur">En euros</ToggleButton>
            <ToggleButton value="pct">En %</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        <Matrix
          data={data}
          rows={rows}
          measure={profitUnit === "pct" ? "margin" : "profit"}
          onOpenClient={onOpenClient}
        />
      </Section>
    </Stack>
  );
};

const toneToCard = (tone: string) =>
  tone === "bad"
    ? ("error" as const)
    : tone === "warn"
      ? ("warning" as const)
      : undefined;

const Matrix: React.FC<{
  data: TabProps["data"];
  rows: ControlBoardRow[];
  measure: Measure;
  onOpenClient: (id: string) => void;
}> = ({ data, rows, measure, onOpenClient }) => {
  const theme = useTheme();
  const months = Array.from({ length: data.lastMonth }, (_, i) => i + 1);
  const isMargin = measure === "margin";
  const span = isMargin ? 2 : 3;

  const clients = useMemo(() => {
    const byClient = groupBy(rows, (r) => r.clientId);
    return data.clients
      .filter((c) => byClient.has(c.id))
      .map((c) => ({ ...c, rows: byClient.get(c.id) as ControlBoardRow[] }));
  }, [rows, data]);

  const planned = (t: Totals) =>
    measure === "sale" ? t.plannedSale : t.plannedProfit;
  const real = (t: Totals) => (measure === "sale" ? t.sale : t.profit);

  const cells = (list: ControlBoardRow[], bold = false) => {
    const weight = bold ? 600 : undefined;
    if (!list.length)
      return Array.from({ length: span }, (_, i) => (
        <Num
          key={i}
          muted
          sx={{
            borderLeft:
              i === 0 ? `1px solid ${theme.palette.grey[100]}` : undefined,
          }}
        >
          ·
        </Num>
      ));
    const t = aggregate(list);
    const border = { borderLeft: `1px solid ${theme.palette.grey[100]}` };
    if (isMargin) {
      return [
        <Num key="p" muted sx={border}>
          {pct(t.plannedMargin, 1)}
        </Num>,
        <Num key="r" sx={{ fontWeight: weight }}>
          {Number.isFinite(t.margin) ? (
            <Signal
              text={pct(t.margin, 1)}
              tone={toneOfMarginDiff(t.margin - t.plannedMargin)}
            />
          ) : (
            "—"
          )}
        </Num>,
      ];
    }
    const p = planned(t);
    const r = real(t);
    const ratio = p ? r / p : NaN;
    return [
      <Num key="p" muted sx={border}>
        {eur(p)}
      </Num>,
      <Num key="r" sx={{ fontWeight: weight }}>
        {eur(r)}
      </Num>,
      <Num key="c">
        {p ? (
          <Signal text={pctSigned(ratio - 1)} tone={toneOfCompliance(ratio)} />
        ) : (
          "—"
        )}
      </Num>,
    ];
  };

  const sub = isMargin ? ["Previsto", "Real"] : ["Previsto", "Real", "%"];
  const stickyCol = {
    position: "sticky" as const,
    left: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    minWidth: 200,
  };
  const byMonthAll = groupBy(rows, (r) => r.month);

  return (
    <TableContainer sx={{ maxHeight: "72vh" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...stickyCol, zIndex: 3 }} />
            {months.map((m) => (
              <TableCell
                key={m}
                colSpan={span}
                align="center"
                sx={{ fontWeight: 600 }}
              >
                {MONTHS[m]}
              </TableCell>
            ))}
            <TableCell colSpan={span} align="center" sx={{ fontWeight: 600 }}>
              Total
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ ...stickyCol, zIndex: 3, top: 33 }}>
              Cliente
            </TableCell>
            {[...months, 0].flatMap((m) =>
              sub.map((s) => (
                <Num key={`${m}-${s}`} sx={{ top: 33 }}>
                  {s}
                </Num>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {clients.map((c) => {
            const byMonth = groupBy(c.rows, (r) => r.month);
            return (
              <TableRow key={c.id} hover>
                <TableCell sx={stickyCol}>
                  <Link
                    component="button"
                    underline="hover"
                    onClick={() => onOpenClient(c.id)}
                    sx={{ fontWeight: 600, textAlign: "left" }}
                  >
                    {c.name}
                  </Link>
                </TableCell>
                {months.flatMap((m) => cells(byMonth.get(m) ?? []))}
                {cells(c.rows, true)}
              </TableRow>
            );
          })}
          <TableRow sx={{ "& td": { fontWeight: 700 } }}>
            <TableCell sx={stickyCol}>Total mensual</TableCell>
            {months.flatMap((m) => cells(byMonthAll.get(m) ?? [], true))}
            {cells(rows, true)}
          </TableRow>
        </TableBody>
      </Table>
      {clients.length === 0 && (
        <Box p={2}>
          <Typography variant="body2" color="text.secondary">
            No hay líneas de briefing en el año.
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

export default BoardTab;
